import * as Sentry from '@sentry/remix';
import { useEffect, useState } from 'react';

import {
  getPendingRegistrationEmail,
  PENDING_REGISTRATION_EMAIL_CHANGED_EVENT,
  PENDING_REGISTRATION_EMAIL_KEY
} from '~/utils/browser';

import useUser from './useUser';

const PENDING_NOTIFICATION_PRODUCTS_CHANGED_EVENT =
  'pending-notification-products-changed';

let cachedEmail: string | null = null;
let cachedProductIds: string[] = [];
let inflight: Promise<void> | null = null;

function emitProductsChanged() {
  window.dispatchEvent(new Event(PENDING_NOTIFICATION_PRODUCTS_CHANGED_EVENT));
}

async function refreshPendingProductIds(force = false) {
  const email = getPendingRegistrationEmail();

  if (!email) {
    if (cachedEmail !== null || cachedProductIds.length) {
      cachedEmail = null;
      cachedProductIds = [];
      emitProductsChanged();
    }

    return;
  }

  if (!force && email === cachedEmail) return;

  if (inflight) return inflight;

  inflight = (async () => {
    try {
      const response = await fetch(
        `/bolt/register/pending?email=${encodeURIComponent(email)}`
      );

      const data = response.ok
        ? ((await response.json()) as { productIds?: string[] })
        : { productIds: [] };

      cachedEmail = email;
      cachedProductIds = data.productIds ?? [];
      emitProductsChanged();
    } catch (error) {
      Sentry.captureException(error, {
        tags: { feature: 'pending-notification-confirmation' }
      });
    } finally {
      inflight = null;
    }
  })();

  return inflight;
}

export function usePendingNotificationsSync() {
  useEffect(() => {
    const syncFromServer = (force = false) => {
      void refreshPendingProductIds(force);
    };

    const syncFromServerForced = () => syncFromServer(true);

    const syncOnStorageChange = (event: StorageEvent) => {
      if (event.key === null || event.key === PENDING_REGISTRATION_EMAIL_KEY) {
        syncFromServerForced();
      }
    };

    syncFromServer();

    window.addEventListener(
      PENDING_REGISTRATION_EMAIL_CHANGED_EVENT,
      syncFromServerForced
    );
    window.addEventListener('storage', syncOnStorageChange);

    return () => {
      window.removeEventListener(
        PENDING_REGISTRATION_EMAIL_CHANGED_EVENT,
        syncFromServerForced
      );
      window.removeEventListener('storage', syncOnStorageChange);
    };
  }, []);
}

export default function useIsPendingConfirmation(
  productId: number,
  isNotified: boolean
) {
  const { isLoggedIn } = useUser();

  const [pendingProductIds, setPendingProductIds] =
    useState<string[]>(cachedProductIds);

  useEffect(() => {
    const sync = () => setPendingProductIds(cachedProductIds);

    sync();

    window.addEventListener(PENDING_NOTIFICATION_PRODUCTS_CHANGED_EVENT, sync);

    return () => {
      window.removeEventListener(
        PENDING_NOTIFICATION_PRODUCTS_CHANGED_EVENT,
        sync
      );
    };
  }, []);

  return (
    !isLoggedIn && !isNotified && pendingProductIds.includes(String(productId))
  );
}
